import { Component, Inject, OnInit, AfterViewInit, ChangeDetectorRef, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { GeocodingService } from '../../services/geocoding.service';
import { environment } from 'src/environments/environment';
import { SmartMapComponent } from '../smart-map/smart-map.component';

@Component({
    selector: 'app-map-dialog',
    templateUrl: './map-dialog.component.html',
    styleUrls: ['./map-dialog.component.scss']
})
export class MapDialogComponent implements OnInit, AfterViewInit {
    width: string = '100%';
    height: string = '30vh';
    lat: number = 0;
    lng: number = 0;

    @ViewChild(SmartMapComponent) currentMap: SmartMapComponent | undefined;

    address = '';
    geoLocation: any = {};
    loading: boolean = false;
    changeCenterAddPinValue: string = '';
    private markerCounter: number = 0;
    private mapReady: boolean = false;

    get setCenterJson(): string {
        return this.geoLocation && this.geoLocation.lat && this.geoLocation.lng
            ? JSON.stringify(this.geoLocation)
            : '';
    }

    constructor(
        private translate: TranslateService,
        public dialogRef: MatDialogRef<MapDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private geocodingService: GeocodingService,
        private cdr: ChangeDetectorRef
    ) {
        if (data) {
            this.height = data.height || this.height;
            this.lat = +data.lat || this.lat;
            this.lng = +data.lng || this.lng;
        }

        if (this.lat && this.lng) {
            this.geoLocation = { lat: this.lat, lng: this.lng };
            this.updateChangeCenterAddPin();
            this.geocodeLatLng(this.lat, this.lng);
        }
        else if (navigator && navigator.geolocation) {
            navigator.geolocation.getCurrentPosition((position: any) => {
                this.geoLocation = { lat: position.coords.latitude, lng: position.coords.longitude };
                this.updateChangeCenterAddPin();
                this.geocodeLatLng(position.coords.latitude, position.coords.longitude);
            }, (error: any) => {
                // Geolocation error - use default location
                if (environment.lnglatdefault) {
                    this.geoLocation = { lat: environment.lnglatdefault.lat, lng: environment.lnglatdefault.lng };
                    this.updateChangeCenterAddPin();
                }
            });
        } else {
            // No geolocation available - use default location
            if (environment.lnglatdefault) {
                this.geoLocation = { lat: environment.lnglatdefault.lat, lng: environment.lnglatdefault.lng };
                this.updateChangeCenterAddPin();
            }
        }
    }

    private updateChangeCenterAddPin(): void {
        if (this.geoLocation && this.geoLocation.lat && this.geoLocation.lng) {
            // Only update if map is ready, or if it's the initial setup
            if (this.mapReady || this.markerCounter === 0) {
                // Increment counter to ensure the value changes and triggers the setter
                this.markerCounter++;
                // Create a new JSON string with a unique value to ensure the setter is triggered
                const locationWithCounter = {
                    lat: this.geoLocation.lat,
                    lng: this.geoLocation.lng,
                    _counter: this.markerCounter
                };
                this.changeCenterAddPinValue = JSON.stringify(locationWithCounter);
            }
        }
    }

    ngOnInit(): void {
        this.loading = true;
    }

    ngAfterViewInit(): void {
        // Ensure the initial marker is set after the view is initialized
        if (this.geoLocation && this.geoLocation.lat && this.geoLocation.lng) {
            this.updateChangeCenterAddPin();
        }
    }

    onMapSelect(event: any) {
        if (!this.data?.disablePinClick) {
            const lat = event.lat;
            const lng = event.lng;

            if (lat !== undefined && lng !== undefined) {
                this.geoLocation = { lat, lng };
                // Always update marker when user clicks (map should be ready by then)
                this.updateChangeCenterAddPin();
                this.geocodeLatLng(lat, lng);
            }
        }
    }

    onGeoLocation(event: any) {
        if (event && event.lat !== undefined && event.lng !== undefined) {
            this.geoLocation = event;
            this.geocodeLatLng(event.lat, event.lng);
        }
    }

    onAddressPinData(event: any) {
        if (event) {
            this.address = event;
        }
    }

    async geocodeLatLng(lat: number, lng: number) {
        try {
            const lang = this.translate.currentLang || "en";
            const address = await this.geocodingService.resolveAddress(lat, lng, lang);
            if (address) {
                this.address = address;
                this.geoLocation = { lat, lng };
            }
        } catch (e) {
            // Address resolution failed
        }
    }

    onConfirm() {
        this.dialogRef.close({ address: this.address, coordinates: this.geoLocation });
    }

    onTilesLoaded() {
        this.loading = false;
        this.mapReady = true;

        if (this.currentMap && this.data?.rotation_start_at !== undefined && this.data?.rotation_end_at !== undefined && this.data?.shape_length !== undefined) {
            // console.log('MapDialog adding sector with data:', this.data);
            this.currentMap.addSector('camera-sector', {
                center: { lat: this.lat, lng: this.lng },
                radius: +this.data.shape_length,
                startAngle: (+this.data.rotation_start_at) * 30,
                endAngle: (+this.data.rotation_end_at) * 30,
                color: this.data.camera_field_view_color || 'red',
                showBorder: true,
                editable: false,
                showDots: false,
                showTooltip: false,
                clickable: false
            });
        }

        // Map is ready, ensure marker is set
        if (this.geoLocation && this.geoLocation.lat && this.geoLocation.lng) {
            // Use a small delay to ensure the map is fully initialized
            setTimeout(() => {
                this.updateChangeCenterAddPin();
            }, 200);
        }
    }
}
